// src/portal/portalTags.js
//
// THE SUBMITTER'S TAGS, ON THE WAY TO route 4.
//
// ── TWO MODELS, AND ONLY ONE OF THEM CAN SAY "UNTAGGED" ─────────────────────
//
// THE TAG MODEL is `Map<ordinal, tag>`. An untagged feature is SIMPLY NOT IN
// IT. There is no sentinel, no null entry, no "untagged" string — untagged has
// no encoding at all, so there is no fourth value for anything to leak.
//
// THE RENDER MODEL is the GeoJSON handed to MapLibre, where a feature does
// carry `submitter_tag: null`, because a paint expression needs something to
// test against. That null exists only inside the map source. Nothing
// serialises the render model, and this file is the only thing that builds the
// wire payload.
//
// That separation is the mechanism, not a rule someone has to remember. A rule
// holds until it is convenient; a value that cannot be represented does not
// need anyone's discipline.
//
// ── WHY THE SHAPE MATTERS, MEASURED ─────────────────────────────────────────
//
// route 4 requires `Array.isArray(tags)` (planReviewSubmission.js validateTags).
// The panel shipped holding `useState({})`, and BOTH of these refuse
// identically:
//
//   {}                      -> 400 tags_missing
//   {"0":"pad","1":"road"}  -> 400 tags_missing
//
// So a tagging control written into that object would have produced the exact
// failure a submitter already hit, and read as "the fix didn't take". The
// container is corrected here, on its own, before any control writes to it.
//
// ── ORDINALS ARE THE SERVER'S, ZERO-BASED, AND NUMBERS ──────────────────────
//
// prepareFeatures assigns `ordinal: i` from a zero-based loop. Three ways that
// goes wrong, all measured against the real validateTags:
//
//   1-based        [{ordinal:1},{ordinal:2},{ordinal:3}]  -> tag_unknown_feature
//   string keys    [{ordinal:"0"}]                        -> tag_ordinal_invalid
//   stale ordinal  a feature no longer in the layer       -> tag_unknown_feature
//
// The 1-based case is the dangerous one because it fails MISLEADINGLY: with
// three features it reports only `Tagged feature(s) that are not in this layer:
// 3`, since 1 and 2 are legitimate ordinals. A wholly shifted mapping reads as
// one odd feature.
//
// Two defences. `tagsForWire` iterates THE FEATURES, taking each ordinal from
// route 3's own response rather than from a Map key — a key cannot be off by
// one if it is never the source. And `Object.keys()` / `Object.entries()` are
// never used on the tag model, because they stringify: that is precisely how a
// number becomes "0".

import { SUBMITTER_TAGS, isSubmitterTag } from "../../../admin-api/lib/plan-review/submitterTags.js";

export { SUBMITTER_TAGS, isSubmitterTag };

/** An empty tag model. */
export function emptyTags() {
  return new Map();
}

/**
 * Assert `tag` about `ordinal`. Returns a NEW Map — React state.
 * @throws {TypeError} on a value outside SUBMITTER_TAGS
 */
export function setTag(tags, ordinal, tag) {
  if (!Number.isInteger(ordinal)) {
    throw new TypeError(`ordinal must be an integer, got ${JSON.stringify(ordinal)}`);
  }
  if (!isSubmitterTag(tag)) {
    throw new TypeError(`"${String(tag)}" is not a submitter tag. Use one of: ${SUBMITTER_TAGS.join(", ")}.`);
  }
  const next = new Map(tags);
  next.set(ordinal, tag);
  return next;
}

/**
 * Withdraw the assertion about `ordinal`, returning it to UNTAGGED.
 *
 * ── WHY REMOVAL EXISTS ──────────────────────────────────────────────────────
 *
 * The first draft of this had no removal, reasoning that every feature must be
 * tagged to submit, so untagged is only ever the initial state and returning to
 * it just reaches a blocked one.
 *
 * That reasoning is about the END state and ignores the MIDDLE. A submitter
 * fifteen features into a plan who loses track of which ones they have done has
 * no way to mark uncertainty — and the thing they will reach for is tagging it
 * `other`, which puts a FALSE ASSERTION in a record the reviewer relies on and
 * the export carries, rather than an absence of one.
 *
 * Untagged-and-blocked is the honest state for "I do not know yet". The submit
 * button already says what is missing, so the block is informative rather than
 * mysterious.
 */
export function clearTag(tags, ordinal) {
  if (!tags.has(ordinal)) return tags;   // identity, so React skips the render
  const next = new Map(tags);
  next.delete(ordinal);
  return next;
}

/** The tag asserted about `ordinal`, or null if none has been. */
export function tagOf(tags, ordinal) {
  return tags.get(ordinal) ?? null;
}

/** Ordinals of `features` with no assertion yet, in the features' own order. */
export function untaggedOrdinals(tags, features = []) {
  return features.filter((f) => !tags.has(f.ordinal)).map((f) => f.ordinal);
}

/** {tagged, total} for the progress line. Counts against FEATURES, not Map size. */
export function tagProgress(tags, features = []) {
  const total = features.length;
  return { tagged: features.filter((f) => tags.has(f.ordinal)).length, total };
}

/**
 * THE ONLY PRODUCER OF route 4's `tags`.
 *
 * Iterates the FEATURES — route 3's, so every ordinal is the server's own,
 * passed through untouched. A Map key is never the source of an ordinal, and
 * an entry for a feature that is no longer in the layer is dropped rather than
 * sent, because `tag_unknown_feature` is a refusal about something the
 * submitter cannot see.
 *
 * Incomplete by design: it emits what HAS been asserted and does not pad. The
 * submit button is disabled while anything is untagged, so `tag_incomplete`
 * should be unreachable in normal use — but if it is reached, it is true.
 *
 * @throws {TypeError} if the model holds a value outside SUBMITTER_TAGS.
 *   BELT AND BRACES: the Map cannot acquire one through setTag, so reaching
 *   this is a programming error and should fail loudly here rather than
 *   becoming a 400 the submitter has to interpret.
 */
export function tagsForWire(tags, features = []) {
  const out = [];
  for (const f of features) {
    if (!tags.has(f.ordinal)) continue;
    const tag = tags.get(f.ordinal);
    if (!isSubmitterTag(tag)) {
      throw new TypeError(
        `tag model holds "${String(tag)}" for ordinal ${f.ordinal}, which is not a submitter tag`);
    }
    if (!Number.isInteger(f.ordinal)) {
      throw new TypeError(`feature ordinal is not an integer: ${JSON.stringify(f.ordinal)}`);
    }
    out.push({ ordinal: f.ordinal, tag });
  }
  return out;
}
